import { Injectable } from '@angular/core';
import { appSettings } from 'app/appSettings';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { logger } from 'app/core/util/logger';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { Florder } from '../../interfaces/florder.interface';
import { FlordersFilter } from '../../interfaces/florders-filter.interface';
import { FlordersTO } from '../../interfaces/florders.interface';
import { DeliveryMethod, FlowDetail, FlowDetailTO } from '../../store/edit-florder-detail/florder-detail.interface';
import { DatesRange } from '../../util/date.utils';
import { util } from '../../util/util';
import { default as cachedFlow } from './responses/getFlow';
import { default as cachedFlows } from './responses/getFlows';
import { RestService } from './rest.service';

@Injectable({ providedIn: 'root' })
export class FlowRestService {
  constructor(private restService: RestService) {
  }

  getFlows(filter: FlordersFilter, customers: Customer[] = null): Observable<FlordersTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getFlows');
      return of(cachedFlows as { totalItems: number; items: any[] });
    } else {
      const params = this.getQueryParams({}, filter || {}, customers);
      return this.restService.post<FlordersTO>(`/Flow/query`, params);
    }
  }

  getFlowsAsDocument(filter: FlordersFilter, totalItems: number, exportType: string, customers: Customer[] = []): Observable<object> {
    if (filter != null) {
      const params = this.getQueryParams(
        { ExportType: exportType },
        {
          ...filter,
          pageNr: 1,
          pageSize: totalItems,
        },
        customers
      );
      return this.restService.getBlobData(`/Flow/listFlowsAsDocument`, params);
    }
    return this.restService.getBlobData(`/Flow/listFlowsAsDocument`, { ExportType: exportType });
  }

  getFlow(etmOrderNr: string, salesOrderNumber: string, customerIds: string[] = []): Observable<FlowDetailTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getFlow');
      return of(cachedFlow as any);
    } else {
      let url = `/Flow/get?EtmOrderNumber=${etmOrderNr}`;
      if (salesOrderNumber) {
        url += `&SalesOrderNumber=${salesOrderNumber}`;
      }
      if (customerIds && customerIds.length) {
        url += '&' + util.serializeObjectToParams({ customerId: customerIds });
      }
      return this.restService.get<FlowDetailTO>(url);
    }
  }

  getFlowDocument(etmOrderNumber: string, customerIds: string[] = []): Observable<object> {
    let url = `/Flow/getFlowDocument?EtmOrderNumber=${etmOrderNumber}`;
    if (customerIds && customerIds.length) {
      url += '&' + util.serializeObjectToParams({ customerId: customerIds });
    }
    return this.restService.getBlobData(url);
  }

  getDeliveryDates(deliveryMethod: DeliveryMethod, startDate?: string): Observable<DatesRange> {
    // todo : controleren of het antwoord van de server in volgend formaat is { start: "2018-10-15", end: "2018-10-20"}
    const time = moment().format('HH:mm:ss');
    const url = `/Flow/getDeliveryDates?FlowType=${deliveryMethod.type}&FromId=${deliveryMethod.from.id}&ToId=${deliveryMethod.to.id}&time=${time}`;

    return this.restService.get<DatesRange>(url);
  }

  createFlow(customerId: string, florderDetail: FlowDetail): Observable<FlowDetail> {
    const postData = this.createFlowDataCreator(florderDetail, false);
    return this.restService.post<FlowDetail>('/Flow/create', postData, customerId);
  }

  updateFlow(customerId: string, florderDetail: FlowDetail): Observable<FlowDetail> {
    const postData = this.createFlowDataCreator(florderDetail, true);
    return this.restService.post<FlowDetail>('/Flow/update', postData, customerId);
  }

  sendDisputeFlow(customerId: string, florderDetail: FlowDetail): Observable<FlowDetail> {
    const postData = this.createDisputeFlowRequest(florderDetail);
    return this.restService.post<FlowDetail>('/Flow/performDisputeManagement', postData, customerId);
  }

  validateFlowDate(flowDate: string, frequency: 'T' | 'W' | 'D', fromId: string, toId: string, flowWeek): Observable<any> {
    return this.restService.post<any>('/Flow/checkFlowDate', { frequency, flowDate, flowWeek, fromId, toId });
  }

  acceptFlow(flow: Florder): Observable<any> {
    const postData = this.acceptFlowDataCreator(flow);
    return this.restService.post<FlowDetail>('/Flow/accept', postData);
  }

  cancelFlow(flow: Florder): Observable<any> {
    const postData = this.cancelFlowDataCreator(flow);
    return this.restService.post<FlowDetail>('/Flow/cancel', postData);
  }

  getLatestFlows(selectedCustomerId: string): Observable<FlordersTO> {
    return this.restService.get<FlordersTO>(`/Flow/Latest?CustomerId=${selectedCustomerId}`);
  }

  getFlowTypes(selectedCustomerId: string): Observable<any> {
    return this.restService.get<any>(`/Customer/getFlowTypes?date=${util.formatDate(new Date())}&CustomerId=${selectedCustomerId}`);
  }

  private getQueryParams(params: any, filter: FlordersFilter, customers: Customer[] = null): object {
    let customerIdsForFilter = null;
    if (!filter.customers || filter.customers.length === 0) {
      customerIdsForFilter = (customers || []).map(c => c.id);
    } else {
      customerIdsForFilter = filter.customers;
    }
    const newParamsObject = {
      ...params,
      FlowType: filter.flowTypes,
      FlowDateFrom: filter.florderDateFrom,
      FlowDateTo: filter.florderDateTo,
      TransferDateFrom: filter.transferDateFrom,
      TransferDateTo: filter.transferDateTo,
      PackingID: filter.packingIds,
      CustomerRefNumber: filter.customerReferenceNumber || null,
      SenderRefNumber: filter.senderReferenceNumber || null,
      SalesOrderNumber: filter.salesOrderNumber || null,
      EtmOrderNumber: filter.etmDocumentNumber || null,
      Clearing: (filter.clearingYes && filter.clearingNo) || (!filter.clearingYes && !filter.clearingNo) ? null : filter.clearingYes,
      PageNumber: filter.pageNr,
      PageSize: filter.pageSize,
      SortingColumn: filter.sortField,
      SortingType: filter.sortAscending ? 'asc' : 'desc',
      FlowStatus: filter.flowStatuses,
      handshakeDateFrom: filter.handshakeDateFrom,
      handshakeDateTo: filter.handshakeDateTo,
      From: filter.froms,
      To: filter.tos,
      Wholesaler: filter.wholesalers,
      ShipTo: filter.shipTos,
      CustomerId: customerIdsForFilter,
      DefinitiveDateFrom: filter.florderDefinitiveDateFrom,
      DefinitiveDateTo: filter.florderDefinitiveDateTo,
    };
    return newParamsObject;
  }

  private cancelFlowDataCreator(flow: Florder) {
    const postData = {
      etmOrderNumber: flow.etmOrderNumber,
      salesOrderNumber: null, // Can be empty according to SOAP example
      deliveryNumber: null, // Can be empty according to SOAP example
      shipmentNumber: null, // Can be empty according to SOAP example
      updateTime: flow.updateTime,
      updateDate: flow.updateDate,
      updateBy: flow.updateBy,
    };
    return postData;
  }

  private createFlowDataCreator(flowDetail: FlowDetail, isUpdate: boolean) {
    const postData = {
      flowDate: flowDetail.planning.flowDate,
      flowType: flowDetail.deliveryMethod.type,
      fromId: flowDetail.deliveryMethod.from.id,
      toId: flowDetail.deliveryMethod.to.id,
      clearing: flowDetail.deliveryMethod.clearing,
      transporter: flowDetail.deliveryMethod.transporter,
      licensePlate: flowDetail.deliveryMethod.licensePlate,
      customerRefNumber: flowDetail.deliveryMethod.customerReferenceNumber,
      originalReferenceNumber: flowDetail.deliveryMethod.originalReferenceNumber,
      senderRefNumber: flowDetail.deliveryMethod.senderReferenceNumber,
      comments: flowDetail.deliveryMethod.remarks,
      etmOrderNumber: flowDetail.etmOrderNumber,
      salesOrderNumber: flowDetail.salesOrderNumber,
      wholesalerId: (flowDetail.deliveryMethod.wholesaler && flowDetail.deliveryMethod.wholesaler.id && flowDetail.deliveryMethod.wholesaler.id.trim()) || null,
      updateTime: isUpdate ? flowDetail.updateTime : undefined,
      updateDate: isUpdate ? flowDetail.updateDate : undefined,
      updateBy: isUpdate ? flowDetail.updateBy : undefined,

      lineItem: flowDetail.materials.map(m => {
        return {
          itemNumber: !isUpdate || m.isNew ? 0 : m.internalId,
          packingId: m.packingId,
          quantity: m.packingQuantity,
          contentId: m.contentId,
          lineReferenceId: m.lineReferenceId,
        };
      }),
    };
    return postData;
  }

  private createDisputeFlowRequest(florderDetail: FlowDetail) {
    const postData = {
      updateTime: florderDetail.updateTime,
      updateDate: florderDetail.updateDate,
      updateBy: florderDetail.updateBy,
      flowDate: florderDetail.planning.flowDate,
      dispatchDate: null,
      flowType: florderDetail.deliveryMethod.type,
      customerRefNumber: florderDetail.deliveryMethod.customerReferenceNumber,
      originalReferenceNumber: florderDetail.deliveryMethod.originalReferenceNumber,
      senderRefNumber: florderDetail.deliveryMethod.senderReferenceNumber,
      etmOrderNumber: florderDetail.etmOrderNumber,
      salesOrderNumber: florderDetail.salesOrderNumber,
      deliveryNumber: null,
      shipmentNumber: null,
      flowDirection: null,
      fromId: florderDetail.deliveryMethod.from.id,
      toId: florderDetail.deliveryMethod.to.id,
      wholesalerId: (florderDetail.deliveryMethod.wholesaler && florderDetail.deliveryMethod.wholesaler.id && florderDetail.deliveryMethod.wholesaler.id.trim()) || null,
      transporter: florderDetail.deliveryMethod.transporter,
      clearing: florderDetail.deliveryMethod.clearing,
      licensePlate: florderDetail.deliveryMethod.licensePlate,
      comments: florderDetail.deliveryMethod.remarks,

      lineItem: florderDetail.materials.map(m => {
        const lineInDispute = m.status === florderDetail.status;
        return {
          itemNumber: m.isNew ? 0 : m.internalId,
          packingId: m.packingId,
          packingStatus: null,
          quantity: lineInDispute || m.isNew ? m.disputeQuantity : m.definitiveQuantity,
          logisticsVarietyPacking: null,
          contentId: m.contentId,
          lineReferenceId: m.lineReferenceId,
        };
      }),
    };
    return postData;
  }

  private acceptFlowDataCreator(flow: Florder) {
    const postData = {
      flowType: flow.flowType,
      flowDate: flow.flowDate,
      customerRefNumber: flow.customerRefNumber,
      etmOrderNumber: flow.etmOrderNumber,
      salesOrderNumber: flow.salesOrderNumber,
      deliveryNumber: flow.deliveryNumber,
      shipmentNumber: Array.isArray(flow.shipmentNumbers) ? flow.shipmentNumbers.join(',') : flow.shipmentNumbers,
      senderRefNumber: flow.senderRefNumber,
      fromId: flow.from.id,
      toId: flow.to.id,
      transporter: flow.transporter,
      clearing: (typeof flow.clearing === 'string') ? flow.clearing?.toLowerCase() === 'true' : flow.clearing,
      licensePlate: flow.licensePlate,
      comments: flow.comments,
      wholesalerId: flow.wholesaler && flow.wholesaler.id,
      Test: false,
      updateTime: flow.updateTime,
      updateDate: flow.updateDate,
      updateBy: flow.updateBy,

      LineItem: flow.flowLineItems.map(item => {
        return {
          itemNumber: item.isNew ? 0 : item.itemNumber,
          packingId: item.packingId,
          packingStatus: util.cleanValue(item.packingStatus),
          quantity: flow.editOriginalQuantity ? item.originalQuantity : item.definitiveQuantity,
          contentId: item.contentId,
          lineReferenceId: item.lineReferenceId,
        };
      }),
    };
    return postData;
  }
}
